"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  fetchCitiesClient,
  localeToGogoLanguageId,
  type GogoCityRecord,
} from "@/lib/gogoMasterDataClient";

const PAGE_SIZE = 100;

function cityLabel(c: GogoCityRecord): string {
  const name = (c.City ?? "").trim();
  if (name) return name;
  const key = (c.CityKey ?? "").trim();
  if (key) return key;
  return `City #${c.CityId}`;
}

export type ContactCitySelectProps = {
  cityId: number | string | null;
  cityName: string;
  onChange: (next: { cityId: number | string; cityName: string } | null) => void;
};

/** Searchable city field backed by the same Gogo cities API as `NavCitySelector`. */
export function ContactCitySelect({
  cityId,
  cityName,
  onChange,
}: ContactCitySelectProps) {
  const { locale, t, isRtl } = useLanguage();
  const languageId = localeToGogoLanguageId(locale);
  const wrapRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [cities, setCities] = useState<GogoCityRecord[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearchKey, setDebouncedSearchKey] = useState("");

  useEffect(() => {
    if (!open) {
      setSearchInput("");
      setDebouncedSearchKey("");
      return;
    }
    const id = window.setTimeout(() => {
      setDebouncedSearchKey(searchInput.trim());
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput, open]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(false);
      setListPage(1);
      try {
        const { records, totalRecords: total } = await fetchCitiesClient(
          languageId,
          {
            page: 1,
            limit: PAGE_SIZE,
            searchKey: debouncedSearchKey || undefined,
          }
        );
        if (!cancelled) {
          setCities(records);
          setTotalRecords(total);
        }
      } catch {
        if (!cancelled) {
          setLoadError(true);
          setCities([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, languageId, debouncedSearchKey]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const hasMore = cities.length > 0 && cities.length < totalRecords;

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    const nextPage = listPage + 1;
    setLoadingMore(true);
    try {
      const { records } = await fetchCitiesClient(languageId, {
        page: nextPage,
        limit: PAGE_SIZE,
        searchKey: debouncedSearchKey || undefined,
      });
      setCities((prev) => {
        const seen = new Set(prev.map((c) => c.CityId));
        const merged = [...prev];
        for (const r of records) {
          if (!seen.has(r.CityId)) {
            seen.add(r.CityId);
            merged.push(r);
          }
        }
        return merged;
      });
      setListPage(nextPage);
    } catch {
      /* ignore */
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, languageId, listPage, loadingMore, debouncedSearchKey]);

  const selectCity = (c: GogoCityRecord) => {
    onChange({ cityId: c.CityId, cityName: cityLabel(c) });
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <button
        type="button"
        id="contact-city-trigger"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 rounded-lg border border-[#E5E5E5] bg-white px-4 py-3 text-start text-[#130F0E] transition-colors hover:border-[#DD0428]/30 focus:border-[#DD0428] focus:outline-none focus:ring-2 focus:ring-[#DD0428]/20"
      >
        <span className="min-w-0 flex-1 truncate">
          {cityName.trim()
            ? cityName
            : cityId != null
              ? `City #${cityId}`
              : t("contact.selectCity")}
        </span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className={`shrink-0 text-[#6B7280] transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open ? (
        <div
          className="absolute start-0 top-full z-[60] mt-1 flex max-h-80 w-full min-w-0 max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-[#E5E5E5] bg-white shadow-lg"
          role="presentation"
        >
          <div className="shrink-0 border-b border-[#E5E5E5] bg-white px-2 pb-2 pt-1">
            <div className="relative">
              <input
                type="search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t("nav.searchCities")}
                className={`w-full rounded-lg border border-[#E5E5E5] bg-[#FAFAFA] py-2 text-sm text-[#130F0E] placeholder:text-[#9CA3AF] focus:border-[#DD0428]/30 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#DD0428]/20 ${isRtl ? "pe-9 ps-3" : "ps-9 pe-3"}`}
                aria-label={t("nav.searchCities")}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
              />
              <span
                className={`pointer-events-none absolute top-1/2 -translate-y-1/2 text-[#9CA3AF] ${isRtl ? "end-2.5" : "start-2.5"}`}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden
                >
                  <circle cx="11" cy="11" r="8" />
                  <path d="m21 21-4.35-4.35" />
                </svg>
              </span>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1">
            <div role="listbox" aria-label={t("nav.cityList")}>
              {loading ? (
                <p className="px-4 py-6 text-center text-sm text-[#6B7280]">
                  {t("nav.loadingCities")}
                </p>
              ) : loadError ? (
                <p className="px-4 py-6 text-center text-sm text-red-600">
                  {t("nav.citiesError")}
                </p>
              ) : cities.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-[#6B7280]">
                  {t("nav.noCities")}
                </p>
              ) : (
                <>
                  {cities.map((c) => (
                    <button
                      key={c.CityId}
                      type="button"
                      role="option"
                      aria-selected={cityId === c.CityId}
                      onClick={() => selectCity(c)}
                      className="flex w-full items-center gap-2 rounded-lg px-4 py-3 text-start text-sm text-[#130F0E] transition-colors hover:bg-[#F4F4F4] md:py-2.5"
                    >
                      <span className="min-w-0 flex-1 truncate">
                        {cityLabel(c)}
                      </span>
                    </button>
                  ))}
                  {hasMore ? (
                    <div className="border-t border-[#E5E5E5] p-2">
                      <button
                        type="button"
                        disabled={loadingMore}
                        onClick={() => void loadMore()}
                        className="w-full rounded-lg py-2.5 text-center text-sm font-semibold text-[#BD2226] hover:bg-[#FFF5F5] disabled:opacity-50"
                      >
                        {loadingMore
                          ? t("nav.loadingCities")
                          : t("nav.loadMoreCities")}
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
